import {
  type ClipboardEvent,
  type FormEvent,
  type JSX,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
} from 'react';

import classNames from 'classnames';

import {
  BoldTextLeaf,
  CodeTextLeaf,
  HighlightTextLeaf,
  ItalicTextLeaf,
  type Mark,
  StrikethroughTextLeaf,
  StyledTextLeaf,
  SubscriptTextLeaf,
  SuperscriptTextLeaf,
  TextLeaf,
  type TTextLeaf,
  UnderlineTextLeaf,
  UnknownTextLeaf,
} from '@/textLeaf';

interface Props {
  defaultValue: string;
  placeholder: string;
  onChange?: (value: string) => void;
  leaf: TTextLeaf;
  testId: string;
}

const markToComponent = {
  textStyle: StyledTextLeaf,
  highlight: HighlightTextLeaf,
  italic: ItalicTextLeaf,
  code: CodeTextLeaf,
  bold: BoldTextLeaf,
  underline: UnderlineTextLeaf,
  strike: StrikethroughTextLeaf,
  subscript: SubscriptTextLeaf,
  superscript: SuperscriptTextLeaf,
};

const wrapWithMarks = (marks: Mark[], leaf: TTextLeaf, children: ReactNode): JSX.Element => {
  if (marks.length === 0) return <>{children}</>;

  const Component = markToComponent[marks[0].type as keyof typeof markToComponent] || UnknownTextLeaf;

  return (
    <Component mark={marks[0]} leaf={leaf}>
      {wrapWithMarks(marks.slice(1), leaf, children)}
    </Component>
  );
};

export const EditableMergeTagInput = ({ defaultValue, placeholder, onChange, leaf, testId }: Props) => {
  const editableRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = editableRef.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.textContent !== defaultValue) {
      el.textContent = defaultValue;
    }
  }, [defaultValue]);

  const handleInput = (event: FormEvent<HTMLSpanElement>) => {
    // Browsers insert a <br> when contentEditable is cleared, which prevents
    // :empty matching (placeholder + caret sizing). Remove it immediately.
    if (event.currentTarget.innerHTML === '<br>') {
      event.currentTarget.innerHTML = '';
    }
    onChange?.(event.currentTarget.textContent ?? '');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLSpanElement>) => {
    event.preventDefault();
    const text = event.clipboardData.getData('text/plain').replace(/\n/g, ' ');
    document.execCommand('insertText', false, text);
  };

  const editable = (
    // biome-ignore lint/a11y/useSemanticElements: contentEditable span is intentional for style fidelity inside heading/paragraph wrappers
    <span
      ref={editableRef}
      role="textbox"
      aria-multiline="false"
      tabIndex={0}
      contentEditable
      suppressContentEditableWarning
      data-testid={testId}
      data-placeholder={placeholder}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      className={classNames(
        'block w-full min-h-[1em] outline-none caret-[#8b5cf6]',
        'pointer-events-auto',
        'empty:before:content-[attr(data-placeholder)] empty:before:opacity-50 empty:before:pointer-events-none',
      )}
    />
  );

  return <TextLeaf leaf={leaf}>{wrapWithMarks(leaf.marks || [], leaf, editable)}</TextLeaf>;
};
